import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import type {
  FilterValue,
  SortOrder,
  SorterResult,
} from "antd/es/table/interface";
import { Link, useSearchParams } from "react-router-dom";
import {
  clearQuarantineRemnantsManage,
  forceSendRemnantsManage,
  getRemnantsManage,
  recomputeRemnantsManage,
  runPipelineRemnantsManage,
  RemnantsManageMpWarehouse,
  RemnantsManageQuery,
  RemnantsManageRow,
  setBlockRemnantsManage,
  setInSaleRemnantsManage,
  setShareFormulaBulkRemnantsManage,
} from "api/RemnantsManage";
import { getAll as getGoodsTags } from "api/GoodsTags";
import {
  getSheetsSyncLocks,
  isInSaleLockedBySheetsSync,
  SHEETS_SYNC_IN_SALE_TOOLTIP,
  type SheetsSyncLocks,
} from "api/IntegrationSheets";
import FormulaCell, { FormulaField } from "./FormulaCell";
import ReservedQtyCell from "./ReservedQtyCell";
import AvailableExplainCell from "./AvailableExplainCell";

const { Title, Text } = Typography;

const PRESET_VALUES = new Set([
  "reserve_gt_1c",
  "available_lt_mp",
  "mp_ne_target",
  "quarantine",
  "",
]);

const parseNum = (v: string | null): number | null => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const parseIdList = (raw: string | null): number[] => {
  if (!raw?.trim()) return [];
  const seen = new Set<number>();
  const out: number[] = [];
  for (const part of raw.split(",")) {
    const n = Number(part.trim());
    if (!Number.isFinite(n) || n <= 0 || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
};

type Filters = {
  search: string;
  is_combo: "" | "true" | "false";
  preset: RemnantsManageQuery["preset"];
  remnant_1c_min: number | null;
  remnant_1c_max: number | null;
  available_min: number | null;
  available_max: number | null;
  reserved_mp_min: number | null;
  mp_presence: "" | "empty" | "set";
  mp_blocked: "" | "true" | "false";
  mp_quarantine: "" | "true" | "false";
  mp_linked: "" | "true" | "false";
  in_sale: "" | "true" | "false";
  mp_warehouse_ids: number[];
  tag_ids: number[];
};

const emptyFilters: Filters = {
  search: "",
  is_combo: "",
  preset: "",
  remnant_1c_min: null,
  remnant_1c_max: null,
  available_min: null,
  available_max: null,
  reserved_mp_min: null,
  mp_presence: "",
  mp_blocked: "",
  mp_quarantine: "",
  mp_linked: "",
  in_sale: "",
  mp_warehouse_ids: [],
  tag_ids: [],
};

const filtersFromSearchParams = (sp: URLSearchParams): Filters => {
  const presetRaw = sp.get("preset") || "";
  const preset = (
    PRESET_VALUES.has(presetRaw) ? presetRaw : ""
  ) as Filters["preset"];
  const isComboRaw = sp.get("is_combo") || "";
  const is_combo = (
    isComboRaw === "true" || isComboRaw === "false" ? isComboRaw : ""
  ) as Filters["is_combo"];
  const presenceRaw = sp.get("mp_presence") || "";
  const mp_presence = (
    presenceRaw === "empty" || presenceRaw === "set" ? presenceRaw : ""
  ) as Filters["mp_presence"];
  const blockedRaw = sp.get("mp_blocked") || "";
  const mp_blocked = (
    blockedRaw === "true" || blockedRaw === "false" ? blockedRaw : ""
  ) as Filters["mp_blocked"];
  const quarantineRaw = sp.get("mp_quarantine") || "";
  const mp_quarantine = (
    quarantineRaw === "true" || quarantineRaw === "false" ? quarantineRaw : ""
  ) as Filters["mp_quarantine"];
  const linkedRaw = sp.get("mp_linked") || "";
  const mp_linked = (
    linkedRaw === "true" || linkedRaw === "false" ? linkedRaw : ""
  ) as Filters["mp_linked"];
  const inSaleRaw = sp.get("in_sale") || "";
  const in_sale = (
    inSaleRaw === "true" || inSaleRaw === "false" ? inSaleRaw : ""
  ) as Filters["in_sale"];
  const tagIds = parseIdList(sp.get("tag_ids"));
  let mpIds = parseIdList(sp.get("mp_warehouse_ids"));
  if (!mpIds.length) {
    const legacy = parseNum(sp.get("mp_warehouse_id"));
    if (legacy != null) mpIds = [legacy];
  }
  return {
    ...emptyFilters,
    search: sp.get("search") || "",
    is_combo,
    preset,
    remnant_1c_min: parseNum(sp.get("remnant_1c_min")),
    remnant_1c_max: parseNum(sp.get("remnant_1c_max")),
    available_min: parseNum(sp.get("available_min")),
    available_max: parseNum(sp.get("available_max")),
    reserved_mp_min: parseNum(sp.get("reserved_mp_min")),
    mp_presence,
    mp_blocked,
    mp_quarantine,
    mp_linked,
    in_sale,
    mp_warehouse_ids: mpIds,
    tag_ids: tagIds,
  };
};

const writeFiltersToSearchParams = (
  filters: Filters,
  page: number,
  limit: number,
  sort: string,
  order: "ASC" | "DESC"
) => {
  const next = new URLSearchParams();
  const setIf = (k: string, v: string | number | null | undefined) => {
    if (v === undefined || v === null || v === "") return;
    next.set(k, String(v));
  };
  setIf("search", filters.search || undefined);
  setIf("is_combo", filters.is_combo || undefined);
  setIf("preset", filters.preset || undefined);
  setIf("remnant_1c_min", filters.remnant_1c_min);
  setIf("remnant_1c_max", filters.remnant_1c_max);
  setIf("available_min", filters.available_min);
  setIf("available_max", filters.available_max);
  setIf("reserved_mp_min", filters.reserved_mp_min);
  setIf("mp_presence", filters.mp_presence || undefined);
  setIf("mp_blocked", filters.mp_blocked || undefined);
  setIf("mp_quarantine", filters.mp_quarantine || undefined);
  setIf("mp_linked", filters.mp_linked || undefined);
  setIf("in_sale", filters.in_sale || undefined);
  if (filters.mp_warehouse_ids.length) {
    next.set("mp_warehouse_ids", filters.mp_warehouse_ids.join(","));
  }
  if (filters.tag_ids.length) next.set("tag_ids", filters.tag_ids.join(","));
  if (page !== 1) next.set("page", String(page));
  if (limit !== 50) next.set("limit", String(limit));
  if (sort !== "article") next.set("sort", sort);
  if (order !== "ASC") next.set("order", order);
  return next;
};

const ago = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return "—";
  const min = Math.round(ms / 60000);
  if (min < 1) return "только что";
  if (min < 60) return `${min} мин`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} ч`;
  return `${Math.round(h / 24)} д`;
};

const num = (v: number | null | undefined) =>
  v == null || Number.isNaN(v) ? "—" : v;

/** Soft alternating tints so warehouse / MP column groups are easy to scan. */
const WH_COL_COLORS = [
  { cell: "#e6f4ff", head: "#bae0ff" },
  { cell: "#f6ffed", head: "#b7eb8f" },
  { cell: "#fff7e6", head: "#ffd591" },
  { cell: "#f9f0ff", head: "#d3adf7" },
  { cell: "#fff0f6", head: "#ffadd2" },
  { cell: "#e6fffb", head: "#87e8de" },
];

const MP_COL_COLORS = {
  available: { cell: "#fffbe6", head: "#ffe58f" },
  formula: { cell: "#fffbe6", head: "#ffe58f" },
  block: { cell: "#fff2e8", head: "#ffbb96" },
  reserved: { cell: "#f0f5ff", head: "#adc6ff" },
  sent: { cell: "#f9f0ff", head: "#d3adf7" },
  stock: { cell: "#e6fffb", head: "#87e8de" },
  target: { cell: "#f6ffed", head: "#b7eb8f" },
} as const;

const tintProps = (tint: { cell: string; head: string }) => ({
  onHeaderCell: () => ({
    style: { background: tint.head },
  }),
  onCell: () => ({
    style: { background: tint.cell },
  }),
});

/**
 * Server-side column sort (antd sorter: true).
 * First click: larger → smaller; empties stay last via backend NULLS LAST.
 */
const serverSorter = (
  columnKey: string,
  sort: string,
  order: "ASC" | "DESC"
) => ({
  key: columnKey,
  sorter: true as const,
  sortDirections: (columnKey === "article"
    ? ["ascend", "descend"]
    : ["descend", "ascend"]) as SortOrder[],
  sortOrder:
    sort === columnKey
      ? ((order === "DESC" ? "descend" : "ascend") as SortOrder)
      : undefined,
});

const until = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) return "—";
  if (ms <= 0) return "—";
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min} мин`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} ч`;
  return `${Math.round(h / 24)} д`;
};

const isQuarantineActive = (iso?: string | null) =>
  Boolean(iso && new Date(iso).getTime() > Date.now());

const quarantineLabel = (at: string | null, reason: string | null) => {
  if (!isQuarantineActive(at)) return "—";
  return `${reason || "карантин"} · ещё ${until(at)}`;
};

const isWbMarketplace = (marketplace?: string | null) =>
  marketplace === "wildberries";

const mpLabel = (marketplace?: string | null) =>
  isWbMarketplace(marketplace) ? "WB" : "Ozon";

const shortWhName = (name: string) =>
  name.replace(/^Склад\s+/i, "").trim() || name;

const rowKeyOf = (row: RemnantsManageRow) =>
  row.row_key || `${row.article}:${row.mp_warehouse_id ?? 0}`;

type MpView = {
  has: boolean;
  share: string | null;
  shareField: FormulaField;
  shareStateKey: "share_wb" | "share_ozon";
  blocked: boolean;
  quarantine_at: string | null;
  quarantine_reason: string | null;
  reserved: number;
  reserved_synced_at: string | null;
  last_sent: number | null;
  last_sent_at: string | null;
  mp_stock: number | null;
  mp_synced_at: string | null;
  target: number | null;
  need_update: boolean;
  formulas: NonNullable<RemnantsManageRow["wb_mp_formulas"]>;
};

const rowMpView = (
  row: RemnantsManageRow,
  marketplace: string | null | undefined
): MpView => {
  if (isWbMarketplace(marketplace)) {
    return {
      has: row.has_wb,
      share: row.share_wb,
      shareField: "share_in_wildberries",
      shareStateKey: "share_wb",
      blocked: row.wb_blocked,
      quarantine_at: row.wb_quarantine_at,
      quarantine_reason: row.wb_quarantine_reason,
      reserved: row.reserved_wb,
      reserved_synced_at: row.reserved_wb_synced_at,
      last_sent: row.wb_last_sent,
      last_sent_at: row.wb_last_sent_at,
      mp_stock: row.wb_mp_stock,
      mp_synced_at: row.wb_mp_synced_at,
      target: row.wb_target,
      need_update: row.wb_need_update,
      formulas: row.wb_mp_formulas || [],
    };
  }
  return {
    has: row.has_ozon,
    share: row.share_ozon,
    shareField: "share_in_ozon",
    shareStateKey: "share_ozon",
    blocked: row.ozon_blocked,
    quarantine_at: row.ozon_quarantine_at,
    quarantine_reason: row.ozon_quarantine_reason,
    reserved: row.reserved_ozon,
    reserved_synced_at: row.reserved_ozon_synced_at,
    last_sent: row.ozon_last_sent,
    last_sent_at: row.ozon_last_sent_at,
    mp_stock: row.ozon_mp_stock,
    mp_synced_at: row.ozon_mp_synced_at,
    target: row.ozon_target,
    need_update: row.ozon_need_update,
    formulas: row.ozon_mp_formulas || [],
  };
};

const RemnantsManagePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFromUrl = useMemo(
    () => filtersFromSearchParams(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const initialPage = Math.max(1, Number(searchParams.get("page")) || 1);
  const initialLimit = Math.min(
    200,
    Math.max(1, Number(searchParams.get("limit")) || 50)
  );
  const initialSort = searchParams.get("sort") || "article";
  const initialOrder =
    (searchParams.get("order") || "ASC").toUpperCase() === "DESC"
      ? "DESC"
      : "ASC";

  const [rows, setRows] = useState<RemnantsManageRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(initialFromUrl);
  const [searchDraft, setSearchDraft] = useState(initialFromUrl.search);
  const [sort, setSort] = useState(initialSort);
  const [order, setOrder] = useState<"ASC" | "DESC">(initialOrder);
  const [selected, setSelected] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [syncingPipeline, setSyncingPipeline] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [togglingSale, setTogglingSale] = useState(false);
  const [formulaOpen, setFormulaOpen] = useState(false);
  const [formulaDraft, setFormulaDraft] = useState("");
  const [settingFormula, setSettingFormula] = useState(false);
  const [tagOptions, setTagOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [mpWarehouses, setMpWarehouses] = useState<RemnantsManageMpWarehouse[]>(
    []
  );
  const [boundSelfWarehouses, setBoundSelfWarehouses] = useState<
    { id: number; name: string }[]
  >([]);
  const [sheetsSyncLocks, setSheetsSyncLocks] = useState<SheetsSyncLocks>({
    wildberries: false,
    ozon: false,
  });

  const selectedRows = useMemo(
    () => rows.filter((r) => selected.includes(rowKeyOf(r))),
    [rows, selected]
  );
  const selectedArticles = useMemo(
    () => [...new Set(selectedRows.map((r) => r.article))],
    [selectedRows]
  );
  const actionMarketplace = useMemo((): "wildberries" | "ozon" | "both" => {
    const mps = [
      ...new Set(selectedRows.map((r) => r.marketplace).filter(Boolean)),
    ] as string[];
    if (mps.length === 1) {
      return isWbMarketplace(mps[0]) ? "wildberries" : "ozon";
    }
    if (mps.length > 1) return "both";
    if (filters.mp_warehouse_ids.length === 1) {
      const meta = mpWarehouses.find(
        (w) => w.id === filters.mp_warehouse_ids[0]
      );
      return isWbMarketplace(meta?.marketplace) ? "wildberries" : "ozon";
    }
    return "both";
  }, [selectedRows, filters.mp_warehouse_ids, mpWarehouses]);
  const actionLabel =
    actionMarketplace === "both" ? "МП" : mpLabel(actionMarketplace);
  const inSaleLocked = isInSaleLockedBySheetsSync(
    sheetsSyncLocks,
    actionMarketplace,
  );

  useEffect(() => {
    void getSheetsSyncLocks().then(setSheetsSyncLocks);
  }, []);

  useEffect(() => {
    getGoodsTags()
      .then((tags) =>
        setTagOptions(
          [...tags]
            .sort((a, b) =>
              a.name.localeCompare(b.name, "ru", { sensitivity: "base" })
            )
            .map((t) => ({ value: t.id, label: t.name }))
        )
      )
      .catch(() => setTagOptions([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ids = filters.mp_warehouse_ids;
      const selectedMeta = ids
        .map((id) => mpWarehouses.find((w) => w.id === id))
        .filter(Boolean) as RemnantsManageMpWarehouse[];
      const hasWb = selectedMeta.some((w) => isWbMarketplace(w.marketplace));
      const hasOzon = selectedMeta.some((w) => !isWbMarketplace(w.marketplace));
      const onlyWb = hasWb && !hasOzon;
      const onlyOzon = hasOzon && !hasWb;

      const data = await getRemnantsManage({
        page,
        limit,
        search: filters.search || undefined,
        is_combo: filters.is_combo || undefined,
        preset: filters.preset || undefined,
        remnant_1c_min: filters.remnant_1c_min,
        remnant_1c_max: filters.remnant_1c_max,
        available_min: filters.available_min,
        available_max: filters.available_max,
        mp_warehouse_ids: ids.length ? ids : undefined,
        tag_ids: filters.tag_ids.length ? filters.tag_ids : undefined,
        sort,
        order,
        mp_linked: filters.mp_linked || undefined,
        in_sale: filters.in_sale || undefined,
        ...(onlyWb
          ? {
              reserved_wb_min: filters.reserved_mp_min,
              wb_mp_presence: filters.mp_presence || undefined,
              wb_blocked: filters.mp_blocked || undefined,
              wb_quarantine: filters.mp_quarantine || undefined,
            }
          : onlyOzon
          ? {
              reserved_ozon_min: filters.reserved_mp_min,
              ozon_mp_presence: filters.mp_presence || undefined,
              ozon_blocked: filters.mp_blocked || undefined,
              ozon_quarantine: filters.mp_quarantine || undefined,
            }
          : {
              reserved_wb_min: filters.reserved_mp_min,
              reserved_ozon_min: filters.reserved_mp_min,
              wb_mp_presence: filters.mp_presence || undefined,
              ozon_mp_presence: filters.mp_presence || undefined,
              wb_blocked: filters.mp_blocked || undefined,
              ozon_blocked: filters.mp_blocked || undefined,
              wb_quarantine: filters.mp_quarantine || undefined,
              ozon_quarantine: filters.mp_quarantine || undefined,
            }),
      });
      setRows(data.items);
      setTotal(data.total);
      setBoundSelfWarehouses(data.bound_self_warehouses || []);
      const nextMpList = data.mp_warehouses || [];
      if (nextMpList.length) {
        setMpWarehouses(nextMpList);
        const valid = filters.mp_warehouse_ids.filter((id) =>
          nextMpList.some((w) => w.id === id)
        );
        if (!valid.length) {
          const firstActive =
            nextMpList.find((w) => w.is_active) || nextMpList[0];
          setFilters((f) => ({ ...f, mp_warehouse_ids: [firstActive.id] }));
        } else if (valid.length !== filters.mp_warehouse_ids.length) {
          setFilters((f) => ({ ...f, mp_warehouse_ids: valid }));
        }
      }
    } catch {
      setError("Не удалось загрузить остатки");
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, filters, sort, order]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const next = writeFiltersToSearchParams(filters, page, limit, sort, order);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [filters, page, limit, sort, order, searchParams, setSearchParams]);

  const applyPreset = (preset: Filters["preset"]) => {
    setPage(1);
    setFilters((f) => ({ ...f, preset: f.preset === preset ? "" : preset }));
  };

  const runRecompute = async (articles: string[]) => {
    setRecomputing(true);
    try {
      const res = await recomputeRemnantsManage(
        articles.length ? { articles } : {}
      );
      message.success(
        res.scope === "all"
          ? `Пересчитан каталог (${res.articles.length} арт.)`
          : `Пересчитано артикулов: ${res.articles.length}`
      );
      await load();
    } catch {
      message.error("Не удалось пересчитать");
    } finally {
      setRecomputing(false);
    }
  };

  const recomputeClick = () => {
    if (selectedArticles.length) {
      void runRecompute(selectedArticles);
      return;
    }
    Modal.confirm({
      title: "Пересчитать весь каталог?",
      content:
        "Локальный водопад как после правки формулы: available → new_stock, без загрузки с МП и без отправки. Может занять время.",
      okText: "Пересчитать",
      cancelText: "Отмена",
      onOk: () => runRecompute([]),
    });
  };

  const runPipeline = async () => {
    setSyncingPipeline(true);
    try {
      const res = await runPipelineRemnantsManage();
      if (res.queued) {
        message.success(
          "Полный пайплайн поставлен в очередь (загрузка МП → резервы → пересчёт → отправка)"
        );
      } else if (res.reason === "already_queued") {
        message.info("Пайплайн уже в очереди или выполняется");
      } else {
        message.success("Пайплайн запрошен");
      }
    } catch {
      message.error("Не удалось запустить пайплайн");
    } finally {
      setSyncingPipeline(false);
    }
  };

  const forceSend = async () => {
    if (!selectedArticles.length) {
      message.warning("Выберите товары");
      return;
    }
    setSending(true);
    try {
      const res = await forceSendRemnantsManage({
        articles: selectedArticles,
        marketplace: actionMarketplace,
      });
      message.success(
        `Отправлено на МП: WB=${res.sent.wb}, Ozon=${res.sent.ozon} (сброс карантина: WB=${res.marked.wb}, Ozon=${res.marked.ozon})`
      );
      setSelected([]);
      await load();
    } catch {
      message.error("Не удалось отправить");
    } finally {
      setSending(false);
    }
  };

  const clearQuarantine = async () => {
    if (!selectedArticles.length) {
      message.warning("Выберите товары");
      return;
    }
    setClearing(true);
    try {
      const res = await clearQuarantineRemnantsManage({
        articles: selectedArticles,
        marketplace: actionMarketplace,
      });
      message.success(
        `Сброшен карантин WB=${res.cleared.wb}, Ozon=${res.cleared.ozon}, в очередь=${res.enqueued}`
      );
      setSelected([]);
      await load();
    } catch {
      message.error("Не удалось сбросить карантин");
    } finally {
      setClearing(false);
    }
  };

  const setBlock = async (blocked: boolean) => {
    if (!selectedArticles.length) {
      message.warning("Выберите товары");
      return;
    }
    setBlocking(true);
    try {
      const res = await setBlockRemnantsManage({
        articles: selectedArticles,
        marketplace: actionMarketplace,
        blocked,
      });
      message.success(
        blocked
          ? `Блок WB=${res.updated.wb}, Ozon=${res.updated.ozon}`
          : `Снят блок WB=${res.updated.wb}, Ozon=${res.updated.ozon}, в очередь=${res.enqueued}`
      );
      setSelected([]);
      await load();
    } catch {
      message.error(
        blocked ? "Не удалось установить блок" : "Не удалось снять блок"
      );
    } finally {
      setBlocking(false);
    }
  };

  const setInSale = async (in_sale: boolean) => {
    if (!selectedArticles.length) {
      message.warning("Выберите товары");
      return;
    }
    if (inSaleLocked) {
      message.warning(SHEETS_SYNC_IN_SALE_TOOLTIP);
      return;
    }
    setTogglingSale(true);
    try {
      const res = await setInSaleRemnantsManage({
        articles: selectedArticles,
        marketplace: actionMarketplace,
        in_sale,
      });
      message.success(
        in_sale
          ? `В продаже WB=${res.updated.wb}, Ozon=${res.updated.ozon}, в очередь=${res.enqueued}`
          : `Отключено WB=${res.updated.wb}, Ozon=${res.updated.ozon}, в очередь=${res.enqueued}`
      );
      setSelected([]);
      await load();
    } catch {
      message.error(
        in_sale
          ? "Не удалось вернуть в продажу"
          : "Не удалось отключить продажу"
      );
    } finally {
      setTogglingSale(false);
    }
  };

  const applyShareFormulaBulk = async () => {
    if (!selectedArticles.length) {
      message.warning("Выберите товары");
      return;
    }
    const formula = formulaDraft.trim();
    if (!formula) {
      message.warning("Укажите формулу, например x/2");
      return;
    }
    setSettingFormula(true);
    try {
      const res = await setShareFormulaBulkRemnantsManage({
        articles: selectedArticles,
        marketplace: actionMarketplace,
        share_formula: formula,
      });
      message.success(
        `Формула «${res.share_formula}»: WB=${res.updated.wb}, Ozon=${res.updated.ozon}`
      );
      setFormulaOpen(false);
      setFormulaDraft("");
      setSelected([]);
      await load();
    } catch (e: any) {
      message.error(e?.message || "Не удалось задать формулу");
    } finally {
      setSettingFormula(false);
    }
  };

  const patchMpFormula = useCallback(
    (
      article: string,
      marketplace: "wb" | "ozon",
      mpWarehouseId: number,
      next: string | null
    ) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.article !== article || r.mp_warehouse_id !== mpWarehouseId) {
            return r;
          }
          const key =
            marketplace === "wb" ? "wb_mp_formulas" : "ozon_mp_formulas";
          const list = (r[key] || []).map((f) =>
            f.mp_warehouse_id === mpWarehouseId
              ? { ...f, share_formula: next }
              : f
          );
          return { ...r, [key]: list };
        })
      );
    },
    []
  );

  const patchWarehouseComboShare = useCallback(
    (article: string, warehouseId: number, next: string | null) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.article !== article) return r;
          const list = (r.self_warehouses_1c || []).map((s) =>
            s.warehouse_id === warehouseId
              ? { ...s, share_in_combo: next }
              : s
          );
          return { ...r, self_warehouses_1c: list };
        })
      );
    },
    []
  );

  const columns: ColumnsType<RemnantsManageRow> = useMemo(() => {
    const hideComboFormula = filters.is_combo === "true";

    const self1cCols: ColumnsType<RemnantsManageRow> =
      boundSelfWarehouses.length === 0
        ? []
        : boundSelfWarehouses.map((w, whIdx) => {
            const tint =
              WH_COL_COLORS[whIdx % WH_COL_COLORS.length] ?? WH_COL_COLORS[0];
            const tinted = tintProps(tint);
            const children: ColumnsType<RemnantsManageRow> = [
              {
                title: "кол-во",
                width: 70,
                ...tinted,
                ...serverSorter(`self_1c_${w.id}_qty`, sort, order),
                render: (_: unknown, row) => {
                  if (row.is_combo) return "";
                  const slice = (row.self_warehouses_1c || []).find(
                    (s) => s.warehouse_id === w.id
                  );
                  if (!slice?.connected) return "—";
                  return num(slice.remnant_1c);
                },
              },
              {
                title: "резерв",
                width: 70,
                ...tinted,
                ...serverSorter(`self_1c_${w.id}_res`, sort, order),
                render: (_: unknown, row) => {
                  if (row.is_combo) return "";
                  const slice = (row.self_warehouses_1c || []).find(
                    (s) => s.warehouse_id === w.id
                  );
                  if (!slice?.connected) return "—";
                  return num(slice.reserved_mp);
                },
              },
              {
                title: "доступно",
                width: 96,
                ...tinted,
                ...serverSorter(`self_1c_${w.id}_avail`, sort, order),
                render: (_: unknown, row) => {
                  const slice = (row.self_warehouses_1c || []).find(
                    (s) => s.warehouse_id === w.id
                  );
                  if (!slice?.connected) return row.is_combo ? "" : "—";
                  return (
                    <AvailableExplainCell
                      article={row.article}
                      warehouseId={w.id}
                      value={num(slice.available_remnant)}
                    />
                  );
                },
              },
            ];
            if (!hideComboFormula) {
              children.push({
                title: "доля комбо",
                key: `self_1c_${w.id}_combo`,
                width: 120,
                ...tinted,
                render: (_: unknown, row) => {
                  // Combo kits: formula lives on parts only.
                  if (row.is_combo) return "";
                  const slice = (row.self_warehouses_1c || []).find(
                    (s) => s.warehouse_id === w.id
                  );
                  if (!slice?.connected) return "—";
                  return (
                    <FormulaCell
                      article={row.article}
                      warehouseId={w.id}
                      value={slice.share_in_combo ?? row.share_combo}
                      onSaved={(next) =>
                        patchWarehouseComboShare(row.article, w.id, next)
                      }
                      onRecalculated={load}
                    />
                  );
                },
              });
            }
            children.push({
              title: "синхр.",
              key: `self_1c_${w.id}_sync`,
              width: 80,
              ...tinted,
              render: (_: unknown, row) => {
                if (row.is_combo) return "";
                const slice = (row.self_warehouses_1c || []).find(
                  (s) => s.warehouse_id === w.id
                );
                if (!slice?.connected) return "—";
                return ago(slice.remnant_synced_at);
              },
            });
            return {
              title: shortWhName(w.name),
              key: `self_1c_${w.id}`,
              onHeaderCell: () => ({
                style: { background: tint.head },
              }),
              children,
            };
          });

    const cols: ColumnsType<RemnantsManageRow> = [
      {
        title: "Артикул",
        dataIndex: "article",
        ...serverSorter("article", sort, order),
        fixed: "left",
        width: 160,
        render: (article: string, row) => (
          <div>
            <Link to={`/settings/goods?article=${encodeURIComponent(article)}`}>
              <code>{article}</code>
            </Link>
            {row.name ? (
              <div style={{ fontSize: 11, color: "#888" }}>{row.name}</div>
            ) : null}
          </div>
        ),
      },
      {
        title: "Склад МП",
        key: "mp_warehouse",
        fixed: "left",
        width: 200,
        render: (_: unknown, row) => (
          <div>
            <span>
              {mpLabel(row.marketplace)} · {row.mp_warehouse_name || "—"}
            </span>
            {row.mp_is_active === false ? (
              <Tag style={{ marginLeft: 6 }}>выкл</Tag>
            ) : null}
          </div>
        ),
      },
      {
        title: "Комбо",
        dataIndex: "is_combo",
        width: 70,
        render: (v: boolean) =>
          v ? <Tag color="purple">комбо</Tag> : <Tag>нет</Tag>,
      },
    ];
    cols.push(...self1cCols, {
        title: "Доступно МП",
        dataIndex: "available_remnant",
        ...serverSorter("available_remnant", sort, order),
        width: 100,
        ...tintProps(MP_COL_COLORS.available),
        render: num,
      },
      {
        title: "Формула МП",
        key: "formula",
        width: 140,
        ...tintProps(MP_COL_COLORS.formula),
        render: (_: unknown, row) => {
          const view = rowMpView(row, row.marketplace);
          const mpKey = isWbMarketplace(row.marketplace) ? "wb" : "ozon";
          const formula =
            row.mp_warehouse_id != null
              ? view.formulas.find(
                  (f) => f.mp_warehouse_id === row.mp_warehouse_id
                )
              : view.formulas[0];
          if (!formula) return "—";
          return (
            <FormulaCell
              article={row.article}
              mpWarehouseId={formula.mp_warehouse_id}
              value={formula.share_formula}
              onSaved={(next) =>
                patchMpFormula(
                  row.article,
                  mpKey,
                  formula.mp_warehouse_id,
                  next
                )
              }
              onRecalculated={load}
            />
          );
        },
      },
      {
        title: (
          <span title="Не отправляет остатки на МП, но продолжает вытягивать факт с МП">
            Блок
          </span>
        ),
        key: "blocked",
        width: 70,
        ...tintProps(MP_COL_COLORS.block),
        render: (_: unknown, row) => {
          const view = rowMpView(row, row.marketplace);
          return view.blocked ? (
            <Tag color="red" title="Отправка заблокирована, вытягивание остаётся">
              блок
            </Tag>
          ) : (
            "—"
          );
        },
      },
      {
        title: (
          <span title="Связь артикула с карточкой МП (mp_products) для этой интеграции">
            Связь
          </span>
        ),
        key: "mp_linked",
        width: 80,
        ...tintProps(MP_COL_COLORS.block),
        render: (_: unknown, row) =>
          row.mp_linked ? (
            <Tag color="green">есть</Tag>
          ) : (
            <Tag color="default">нет</Tag>
          ),
      },
      {
        title: (
          <span title="Отключение с продажи: целевой остаток 0 (в т.ч. ручные), периодически обнуляет. По умолчанию в продаже">
            Откл.
          </span>
        ),
        key: "in_sale",
        width: 80,
        ...tintProps(MP_COL_COLORS.block),
        render: (_: unknown, row) =>
          row.in_sale === false ? (
            <Tag color="volcano" title="Снят с продажи — target 0">
              откл.
            </Tag>
          ) : (
            "—"
          ),
      },
      {
        title: "Карантин",
        key: "quarantine",
        width: 140,
        ...tintProps(MP_COL_COLORS.block),
        render: (_: unknown, row) => {
          const view = rowMpView(row, row.marketplace);
          return isQuarantineActive(view.quarantine_at) ? (
            <Tag color="orange">
              {quarantineLabel(view.quarantine_at, view.quarantine_reason)}
            </Tag>
          ) : (
            "—"
          );
        },
      },
      {
        title: "Резерв МП",
        onHeaderCell: () => ({
          style: { background: MP_COL_COLORS.reserved.head },
        }),
        children: [
          {
            title: "кол-во",
            width: 120,
            ...tintProps(MP_COL_COLORS.reserved),
            ...serverSorter("reserved_qty", sort, order),
            render: (_: unknown, row) => (
              <ReservedQtyCell
                article={row.article}
                mpWarehouseId={row.mp_warehouse_id}
                value={rowMpView(row, row.marketplace).reserved}
                onRecalculated={load}
              />
            ),
          },
          {
            title: "синхр.",
            key: "reserved_sync",
            width: 80,
            ...tintProps(MP_COL_COLORS.reserved),
            render: (_: unknown, row) =>
              ago(rowMpView(row, row.marketplace).reserved_synced_at),
          },
        ],
      },
      {
        title: "Отправлено",
        onHeaderCell: () => ({
          style: { background: MP_COL_COLORS.sent.head },
        }),
        children: [
          {
            title: "шт",
            width: 60,
            ...tintProps(MP_COL_COLORS.sent),
            ...serverSorter("last_sent", sort, order),
            render: (_: unknown, row) =>
              num(rowMpView(row, row.marketplace).last_sent),
          },
          {
            title: "когда",
            key: "last_sent_at",
            width: 80,
            ...tintProps(MP_COL_COLORS.sent),
            render: (_: unknown, row) =>
              ago(rowMpView(row, row.marketplace).last_sent_at),
          },
        ],
      },
      {
        title: "Сейчас на МП",
        onHeaderCell: () => ({
          style: { background: MP_COL_COLORS.stock.head },
        }),
        children: [
          {
            title: "шт",
            width: 60,
            ...tintProps(MP_COL_COLORS.stock),
            ...serverSorter("mp_stock", sort, order),
            render: (_: unknown, row) =>
              num(rowMpView(row, row.marketplace).mp_stock),
          },
          {
            title: "синхр.",
            key: "mp_sync",
            width: 80,
            ...tintProps(MP_COL_COLORS.stock),
            render: (_: unknown, row) =>
              ago(rowMpView(row, row.marketplace).mp_synced_at),
          },
        ],
      },
      {
        title: "К отправке",
        width: 90,
        ...tintProps(MP_COL_COLORS.target),
        ...serverSorter("target", sort, order),
        render: (_: unknown, row) => {
          const view = rowMpView(row, row.marketplace);
          return (
            <span>
              {num(view.target)}
              {view.need_update ? (
                <Tag color="orange" style={{ marginLeft: 4 }}>
                  queue
                </Tag>
              ) : null}
            </span>
          );
        },
      },
    );
    return cols;
  }, [
    filters.is_combo,
    load,
    patchMpFormula,
    patchWarehouseComboShare,
    boundSelfWarehouses,
    sort,
    order,
  ]);

  const onTableChange = (
    _pag: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<RemnantsManageRow> | SorterResult<RemnantsManageRow>[]
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const columnKey = String(s?.columnKey || s?.field || "");
    if (!columnKey || !s?.order) {
      setSort("article");
      setOrder("ASC");
      setPage(1);
      return;
    }
    setSort(columnKey);
    setOrder(s.order === "descend" ? "DESC" : "ASC");
    setPage(1);
  };

  const scrollX = 1560 + boundSelfWarehouses.length * 420;

  return (
    <div style={{ padding: "8px 0 24px", width: "100%", maxWidth: "100%" }}>
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Управление остатками FBS
          </Title>
          <Text type="secondary">
            Мультивыбор складов МП · строка = артикул × склад МП
            {boundSelfWarehouses.length
              ? ` · 1С колонки: ${boundSelfWarehouses.length} физ. склад(ов)`
              : " · нет привязанных физ. складов"}
          </Text>
        </div>
        <Button onClick={load} loading={loading}>
          Обновить
        </Button>
      </Space>

      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          mode="multiple"
          showSearch
          allowClear
          placeholder="Склады МП"
          style={{ minWidth: 320 }}
          value={filters.mp_warehouse_ids}
          optionFilterProp="label"
          maxTagCount="responsive"
          options={mpWarehouses.map((w) => ({
            value: w.id,
            label: `${mpLabel(w.marketplace)} · ${w.name}${
              w.is_active ? "" : " (выкл)"
            }`,
          }))}
          onChange={(ids: number[]) => {
            setPage(1);
            setSelected([]);
            setFilters((f) => ({
              ...f,
              mp_warehouse_ids: ids,
              mp_blocked: "",
              mp_quarantine: "",
              mp_presence: "",
              reserved_mp_min: null,
            }));
          }}
        />
        <Input.Search
          placeholder="Артикул / название"
          allowClear
          style={{ width: 220 }}
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          onSearch={(v) => {
            setPage(1);
            setFilters((f) => ({ ...f, search: v.trim() }));
          }}
        />
        <Select
          style={{ width: 140 }}
          value={filters.is_combo || undefined}
          allowClear
          placeholder="Комбо"
          options={[
            { value: "true", label: "Только комбо" },
            { value: "false", label: "Без комбо" },
          ]}
          onChange={(v) => {
            setPage(1);
            setFilters((f) => ({ ...f, is_combo: (v as any) || "" }));
          }}
        />
        <Select
          mode="multiple"
          allowClear
          showSearch
          placeholder="Теги"
          style={{ minWidth: 220 }}
          value={filters.tag_ids}
          options={tagOptions}
          optionFilterProp="label"
          onChange={(ids: number[]) => {
            setPage(1);
            setFilters((f) => ({ ...f, tag_ids: ids }));
          }}
        />
        <InputNumber
          placeholder="1С ≥"
          value={filters.remnant_1c_min}
          onChange={(v) => {
            setPage(1);
            setFilters((f) => ({ ...f, remnant_1c_min: v }));
          }}
        />
        <InputNumber
          placeholder="1С ≤"
          value={filters.remnant_1c_max}
          onChange={(v) => {
            setPage(1);
            setFilters((f) => ({ ...f, remnant_1c_max: v }));
          }}
        />
        <InputNumber
          placeholder="Доступно МП ≥"
          value={filters.available_min}
          onChange={(v) => {
            setPage(1);
            setFilters((f) => ({ ...f, available_min: v }));
          }}
        />
        <InputNumber
          placeholder="Доступно МП ≤"
          value={filters.available_max}
          onChange={(v) => {
            setPage(1);
            setFilters((f) => ({ ...f, available_max: v }));
          }}
        />
        <InputNumber
          placeholder="Резерв МП ≥"
          value={filters.reserved_mp_min}
          onChange={(v) => {
            setPage(1);
            setFilters((f) => ({ ...f, reserved_mp_min: v }));
          }}
        />
        <Select
          style={{ width: 160 }}
          value={filters.mp_presence || undefined}
          allowClear
          placeholder="Сейчас на МП"
          options={[
            { value: "empty", label: "— (пусто)" },
            { value: "set", label: "есть число" },
          ]}
          onChange={(v) => {
            setPage(1);
            setFilters((f) => ({
              ...f,
              mp_presence: (v as Filters["mp_presence"]) || "",
            }));
          }}
        />
        <Select
          style={{ width: 130 }}
          value={filters.mp_blocked || undefined}
          allowClear
          placeholder="Блок"
          options={[
            { value: "true", label: "блок" },
            { value: "false", label: "не блок" },
          ]}
          onChange={(v) => {
            setPage(1);
            setFilters((f) => ({ ...f, mp_blocked: (v as any) || "" }));
          }}
        />
        <Select
          style={{ width: 150 }}
          value={filters.mp_linked || undefined}
          allowClear
          placeholder="Связь с МП"
          options={[
            { value: "true", label: "есть связь" },
            { value: "false", label: "нет связи" },
          ]}
          onChange={(v) => {
            setPage(1);
            setFilters((f) => ({
              ...f,
              mp_linked: (v as Filters["mp_linked"]) || "",
            }));
          }}
        />
        <Select
          style={{ width: 150 }}
          value={filters.in_sale || undefined}
          allowClear
          placeholder="Отключение"
          options={[
            { value: "false", label: "отключено" },
            { value: "true", label: "в продаже" },
          ]}
          onChange={(v) => {
            setPage(1);
            setFilters((f) => ({
              ...f,
              in_sale: (v as Filters["in_sale"]) || "",
            }));
          }}
        />
        <Select
          style={{ width: 150 }}
          value={filters.mp_quarantine || undefined}
          allowClear
          placeholder="Карантин"
          options={[
            { value: "true", label: "в карантине" },
            { value: "false", label: "без карантина" },
          ]}
          onChange={(v) => {
            setPage(1);
            setFilters((f) => ({ ...f, mp_quarantine: (v as any) || "" }));
          }}
        />
        <Button
          onClick={() => {
            setSearchDraft("");
            setFilters((f) => ({
              ...emptyFilters,
              mp_warehouse_ids: f.mp_warehouse_ids,
            }));
            setPage(1);
          }}
        >
          Сбросить
        </Button>
      </Space>

      <Space wrap style={{ marginBottom: 12 }}>
        <Text type="secondary">Пресеты:</Text>
        <Button
          type={filters.preset === "reserve_gt_1c" ? "primary" : "default"}
          onClick={() => applyPreset("reserve_gt_1c")}
        >
          Резервы &gt; 1С / брутто комбо
        </Button>
        <Button
          type={filters.preset === "available_lt_mp" ? "primary" : "default"}
          onClick={() => applyPreset("available_lt_mp")}
        >
          Доступно &lt; выложено на МП
        </Button>
        <Button
          type={filters.preset === "mp_ne_target" ? "primary" : "default"}
          onClick={() => applyPreset("mp_ne_target")}
        >
          МП ≠ целевой
        </Button>
        <Button
          type={filters.preset === "quarantine" ? "primary" : "default"}
          onClick={() => applyPreset("quarantine")}
        >
          Карантин
        </Button>
      </Space>

      <Space wrap style={{ marginBottom: 12 }}>
        <Text>
          Выбрано строк: {selected.length} · артикулов: {selectedArticles.length}
        </Text>
        <Button loading={recomputing} onClick={recomputeClick}>
          {selectedArticles.length
            ? `Пересчитать (${selectedArticles.length})`
            : "Пересчитать всё"}
        </Button>
        <Button type="primary" loading={syncingPipeline} onClick={runPipeline}>
          Синхронизировать
        </Button>
        <Button
          loading={sending}
          disabled={!selectedArticles.length}
          onClick={forceSend}
        >
          Отправить на МП ({actionLabel})
        </Button>
        <Button
          loading={clearing}
          disabled={!selectedArticles.length}
          onClick={clearQuarantine}
        >
          Сбросить карантин ({actionLabel})
        </Button>
        <Button
          danger
          loading={blocking}
          disabled={!selectedArticles.length}
          onClick={() => setBlock(true)}
          title="Не отправляет обновления, вытягивание остатков с МП продолжается"
        >
          Заблокировать ({actionLabel})
        </Button>
        <Button
          loading={blocking}
          disabled={!selectedArticles.length}
          onClick={() => setBlock(false)}
        >
          Разблокировать ({actionLabel})
        </Button>
        <Button
          danger
          loading={togglingSale}
          disabled={!selectedArticles.length || inSaleLocked}
          onClick={() => setInSale(false)}
          title={
            inSaleLocked
              ? SHEETS_SYNC_IN_SALE_TOOLTIP
              : "Снять с продажи: целевой остаток 0, в т.ч. ручные"
          }
        >
          Отключить ({actionLabel})
        </Button>
        <Button
          loading={togglingSale}
          disabled={!selectedArticles.length || inSaleLocked}
          onClick={() => setInSale(true)}
          title={inSaleLocked ? SHEETS_SYNC_IN_SALE_TOOLTIP : undefined}
        >
          В продажу ({actionLabel})
        </Button>
        <Button
          disabled={!selectedArticles.length}
          onClick={() => {
            setFormulaDraft("");
            setFormulaOpen(true);
          }}
        >
          Формула ({actionLabel})
        </Button>
      </Space>

      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 12 }} />
      )}

      {(sheetsSyncLocks.wildberries || sheetsSyncLocks.ozon) && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Продажа управляется Google Таблицей"
          description={
            <>
              {sheetsSyncLocks.wildberries ? "Wildberries" : null}
              {sheetsSyncLocks.wildberries && sheetsSyncLocks.ozon ? ", " : null}
              {sheetsSyncLocks.ozon ? "Ozon" : null}
              {" — "}
              кнопки «В продажу» и «Отключить» недоступны. Блокировка и
              карантин по-прежнему вручную.
            </>
          }
        />
      )}

      {!mpWarehouses.length && !loading ? (
        <Alert
          type="info"
          message="Нет складов МП — добавьте склад в настройках интеграции"
          style={{ marginBottom: 12 }}
        />
      ) : null}

      <Table
        size="small"
        bordered
        rowKey={rowKeyOf}
        loading={loading}
        columns={columns}
        dataSource={rows}
        sticky={{ offsetHeader: 53 }}
        scroll={{ x: scrollX }}
        pagination={false}
        onChange={onTableChange}
        rowSelection={{
          selectedRowKeys: selected,
          onChange: (keys) => setSelected(keys as string[]),
        }}
      />

      <div
        style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}
      >
        <Pagination
          current={page}
          pageSize={limit}
          total={total}
          showSizeChanger
          pageSizeOptions={["20", "50", "100", "200"]}
          onChange={(p, ps) => {
            setPage(p);
            setLimit(ps);
          }}
          showTotal={(t) => `Артикулов ${t}`}
        />
      </div>

      <Modal
        title={`Задать формулу (${actionLabel})`}
        open={formulaOpen}
        okText="Назначить"
        cancelText="Отмена"
        confirmLoading={settingFormula}
        onCancel={() => setFormulaOpen(false)}
        onOk={() => void applyShareFormulaBulk()}
      >
        <Text type="secondary">
          x — доступный остаток. Применится к {selectedArticles.length} арт. на{" "}
          {actionLabel}. Примеры: x, x/2, 0
        </Text>
        <Input
          style={{ marginTop: 12 }}
          autoFocus
          placeholder="x/2"
          value={formulaDraft}
          onChange={(e) => setFormulaDraft(e.target.value)}
          onPressEnter={() => void applyShareFormulaBulk()}
        />
      </Modal>
    </div>
  );
};

export default RemnantsManagePage;
